import {
  generateRandomString,
  pkceChallengeFromVerifier,
} from './AuthHelpers';

/* eslint-disable import/prefer-default-export */
/**
 * @file
 *
 * Wrapper around fetch(), and OAuth access token handling operations.
 *
 * To use import getAuthClient, and initialize a client:
 * const auth = getAuthClient();
 */
const refreshPromises = [];

// Dispatched on window when Drupal refuses a refresh token, so the app can send
// a signed-in user back through login instead of carrying on with a dead token.
export const AUTH_EXPIRED_EVENT = 'yourtour:auth-expired';

// A refresh that fails for a reason that is not the server's verdict on the
// token (network error, 5xx, 429, unparseable body) keeps the token, so it has
// to be rate-limited: every API call checks the token, and without a backoff a
// page making one call a second sends one refresh a second (#2075).
const REFRESH_BACKOFF_BASE_MS = 2000;
const REFRESH_BACKOFF_MAX_MS = 5 * 60 * 1000;
// After this many transient failures in a row, stop treating it as transient.
const REFRESH_MAX_TRANSIENT_FAILURES = 5;
const refreshBackoff = { failures: 0, retryAt: 0 };

// localStorage key holding the location the user was heading for when we sent
// them off to Drupal to sign in.
const LOGIN_TARGET_NAME = 'yourtour_login_target';

/**
* Strip the OAuth callback parameters from a query string.
*
* @param {string} search
*   A location.search value, with or without the leading '?'.
* @returns {string}
*   The query string without the OAuth params, with a leading '?' if anything
*   is left, otherwise an empty string.
*/
function stripOauthParams(search) {
  const params = new URLSearchParams(search);
  ['code', 'state', 'error', 'error_description'].forEach((key) => params.delete(key));
  const remaining = params.toString();
  return remaining ? `?${remaining}` : '';
}

/**
* The current location as a path, minus anything the OAuth round trip added.
*
* @returns {string}
*   e.g. '/amenities?mode=preview'.
*/
function currentTargetPath() {
  const { pathname, search, hash } = window.location;
  return `${pathname}${stripOauthParams(search)}${hash}`;
}

/**
* OAuth client factory.
*
* @returns {object}
*   Returns an object of functions with $config injected.
*/
export function getAuthClient() {
  const config = {
    // Base URL of your Drupal site.
    base: process.env.REACT_APP_YOUR_TOUR_CMS_URL,
    // Name to use when storing the token in localStorage.
    token_name: 'yourtour-oauth-token',
    // OAuth client ID - get from Drupal.
    client_id: process.env.REACT_APP_DRUPAL_CONSUMER_UUID,
    // OAuth client secret. Transitional (#1364): the consumer is being made
    // public (PKCE, no secret), so this is only sent while the env var is
    // still set, and is never used to mint guest tokens.
    client_secret: process.env.REACT_APP_DRUPAL_CONSUMER_CLIENT_SECRET,
    // Drupal user role related to this OAuth client.
    scope: 'yourtour_api',
    // Where the request will be redirect after grant is authorized.
    redirect_uri: window.location.origin,
    // Margin of time before the current token expires that we should force a
    // token refresh.
    expire_margin: 0,
  };

  /**
  * Add client_secret to token request params, only if one is configured.
  *
  * An unset env var would otherwise be sent as the string 'undefined'.
  *
  * @param {object} params
  * @returns {object}
  */
  function withClientSecret(params) {
    return config.client_secret
      ? { ...params, client_secret: config.client_secret }
      : params;
  }

  /**
  * Delete the stored OAuth token, effectively ending the user's session.
  */
  function logout() {
    // Remove all localstorage data we stored.
    localStorage.removeItem(config.token_name);

    const redirectUrl = `${process.env.REACT_APP_YOUR_TOUR_CMS_URL}/your-tour/logout?yourtour_redirect=${window.location.href}`;
    window.location = redirectUrl;
  }

  /**
  * Store an OAuth token retrieved from Drupal in localStorage.
  *
  * @param {object} data
  * @param {string} tokenName
  * @returns {object}
  *
  * Returns the token with an additional expires_at property added.
  */
  function saveToken(data, tokenName) {
    const currentToken = { ...data }; // Make a copy of data object.
    currentToken.date = Math.floor(Date.now() / 1000);
    currentToken.expires_at = currentToken.date + currentToken.expires_in;
    localStorage.setItem(tokenName, JSON.stringify(currentToken));
    return currentToken;
  }

  /**
  * Forget a token Drupal will not refresh, and tell the app the session ended.
  *
  * @returns {boolean}
  *   Always false, the value a failed refresh resolves to.
  */
  function dropRefreshedToken() {
    localStorage.removeItem(config.token_name);
    refreshBackoff.failures = 0;
    refreshBackoff.retryAt = 0;
    window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    return false;
  }

  /**
  * Keep the token after a failure that may clear up, but hold off retrying.
  *
  * @param {*} reason
  *   The error or response body, for the console.
  * @returns {boolean}
  *   Always false, the value a failed refresh resolves to.
  */
  function recordTransientRefreshFailure(reason) {
    refreshBackoff.failures += 1;
    console.error('API got an error', reason);
    if (refreshBackoff.failures >= REFRESH_MAX_TRANSIENT_FAILURES) {
      return dropRefreshedToken();
    }
    const delay = Math.min(
      REFRESH_BACKOFF_BASE_MS * 2 ** (refreshBackoff.failures - 1),
      REFRESH_BACKOFF_MAX_MS,
    );
    refreshBackoff.retryAt = Date.now() + delay;
    return false;
  }

  /**
  * Request a new token using a refresh_token.
  *
  * This function is smart about reusing requests for a refresh token. So it is
  * safe to call it multiple times in succession without having to worry about
  * whether a previous request is still processing.
  */
  function refreshToken(refreshTokenObject) {
    if (refreshPromises[refreshTokenObject]) {
      return refreshPromises[refreshTokenObject];
    }

    // Note that the data in the request is different when getting a new token
    // via a refresh_token. grant_type = refresh_token, and do NOT include the
    // scope parameter in the request as it'll cause issues if you do.
    const params = withClientSecret({
      grant_type: 'refresh_token',
      client_id: config.client_id,
      refresh_token: refreshTokenObject,
    });
    const postData = new URLSearchParams(params).toString();

    const newToken = fetch(`${config.base}/oauth/token`, {
      method: 'post',
      headers: new Headers({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
      body: postData,
    })
      .then(async (response) => {
        const data = await response.json().catch(() => null);
        delete refreshPromises[refreshTokenObject];

        if (data?.access_token) {
          refreshBackoff.failures = 0;
          refreshBackoff.retryAt = 0;
          return saveToken(data, config.token_name);
        }

        // invalid_grant and friends, or any other 4xx, is Drupal's final answer
        // on this refresh token: retrying it can only fail the same way.
        // 429 is the exception, since it says "later", not "never".
        const { status } = response;
        const isRateLimited = status === 429;
        const isClientError = status >= 400 && status < 500;
        if (!isRateLimited && (data?.error || isClientError)) {
          console.error('Error refreshing token', data);
          return dropRefreshedToken();
        }

        return recordTransientRefreshFailure(data);
      })
      .catch((err) => {
        delete refreshPromises[refreshTokenObject];
        return recordTransientRefreshFailure(err);
      });

    refreshPromises[refreshTokenObject] = newToken;

    return refreshPromises[refreshTokenObject];
  }

  /**
  * Get the current OAuth token if there is one.
  *
  * Get the OAuth token form localStorage, and refresh it if necessary using
  * the included refresh_token.
  *
  * @returns {Promise}
  *   Returns a Promise that resolves to the current token, or false.
  */
  async function getLocalStorageToken(tokenName) {
    const currentToken = localStorage.getItem(tokenName) !== null
      ? JSON.parse(localStorage.getItem(tokenName))
      : false;

    if (!currentToken) {
      return Promise.reject(new Error('empty token'));
    }

    // A guest token (is_api) minted by an older bundle via client_credentials.
    // Guests are tokenless now and those tokens are being revoked, and a
    // revoked bearer token is refused rather than treated as anonymous, so
    // drop it instead of sending it.
    if (currentToken.is_api) {
      localStorage.removeItem(tokenName);
      return Promise.resolve(false);
    }

    // eslint-disable-next-line camelcase
    const { expires_at, refresh_token } = currentToken;
    // eslint-disable-next-line camelcase
    if (expires_at - config.expire_margin < Date.now() / 1000) {
      // A token with no refresh_token (e.g. an old client_credentials guest
      // token without is_api) cannot be refreshed. Calling refreshToken() with an undefined
      // one is a guaranteed-to-fail POST to /oauth/token, so skip straight to
      // the same outcome a failed refresh produces: drop the token and let the
      // caller re-auth once.
      // eslint-disable-next-line camelcase
      if (!refresh_token) {
        localStorage.removeItem(tokenName);
        return Promise.resolve(false);
      }
      // A recent refresh failed transiently: go out anonymously until the
      // backoff lapses rather than POST again.
      if (Date.now() < refreshBackoff.retryAt) {
        return Promise.resolve(false);
      }
      return refreshToken(refresh_token);
    }
    return Promise.resolve(currentToken);
  }

  /**
  * Login to Drupal and get authorization code.
  */
  async function preLogin() {
    // Stash where the user was actually heading. The registered consumer only
    // matches on the host, but the app clears the path once it has swapped the
    // code for a token, so the destination has to survive the round trip here
    // rather than in the redirect_uri.
    localStorage.setItem(LOGIN_TARGET_NAME, currentTargetPath());

    // Create and store a random "state" value.
    const state = generateRandomString();
    localStorage.setItem('pkce_state', state);

    // Create and store a new PKCE code_verifier (the plaintext random secret)
    const codeVerifier = generateRandomString();
    localStorage.setItem('pkce_code_verifier', codeVerifier);

    // Hash and base64-urlencode the secret to use as the challenge
    const codeChallenge = await pkceChallengeFromVerifier(codeVerifier);

    // Build the authorization URL
    const rawParams = {
      response_type: 'code',
      client_id: config.client_id,
      state,
      scope: config.scope,
      code_challenge: codeChallenge,
      redirect_uri: window.location.origin,
      code_challenge_method: 'S256',
    };
    const params = new URLSearchParams(rawParams).toString();
    // This url expires.
    const url = `${config.base}/oauth/authorize?${params} `;

    // Redirect the user to Drupal to sign in.
    window.location = url;
  }

  /**
   * Get Oauth Token
   * @param {string} code - The authorization code from OAuth callback
   * @param {string} urlState - Optional state parameter from URL (if not
   *   provided, reads from current URL)
   */
  async function login(code, urlState = null) {
    const localState = localStorage.getItem('pkce_state');
    const codeVerifier = localStorage.getItem('pkce_code_verifier');

    // Use provided state or read from current URL
    let state = urlState;
    let error = null;
    if (state === null) {
      const url = new URL(window.location);
      const params = new URLSearchParams(url.search);
      error = params.get('error');
      state = params.get('state');
    }

    if (error || !code) {
      const message = error || 'No code parameter found in URL.';
      return Promise.reject(new Error(message));
    }

    if (localState !== state) {
      console.error('PKCE state mismatch - localStorage:', localState, 'URL:', state);
      return Promise.reject(new Error('Invalid PCKE state.'));
    }

    const postDataParams = withClientSecret({
      grant_type: 'authorization_code',
      client_id: config.client_id,
      scope: config.scope,
      code,
      code_verifier: codeVerifier,
      redirect_uri: config.redirect_uri,
    });

    const postData = new URLSearchParams(postDataParams).toString();
    try {
      const response = await fetch(`${config.base}/oauth/token`, {
        method: 'post',
        headers: new Headers({
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
        body: postData,
      });
      const data = await response.json();
      if (data.error) {
        return Promise.reject(new Error(`Error retrieving OAuth token: ${data.error} `));
      }

      // Clean these up since we don't need them anymore.
      localStorage.removeItem('pkce_state');
      localStorage.removeItem('pkce_code_verifier');

      const token = saveToken(data, config.token_name);

      // Send the user on to the deep link they asked for. The token is already
      // in localStorage, so the reload comes back authenticated and skips the
      // OAuth flow entirely.
      const target = localStorage.getItem(LOGIN_TARGET_NAME);
      localStorage.removeItem(LOGIN_TARGET_NAME);
      if (target && target !== currentTargetPath()) {
        window.location.replace(`${window.location.origin}${target}`);
      }

      return token;
    } catch (err) {
      return Promise.reject(new Error(`API error: ${err} `));
    }
  }

  /**
   * Continue as a guest, without a token.
   *
   * Guests call the API anonymously. There is no secretless way to mint a
   * guest token, and a secret in a public bundle lets anyone mint one (#1364).
   * Resolves a marker so callers can tell "guest" from "auth failed"; nothing
   * is stored, so no Authorization header is sent.
   */
  async function noLoginAuth() {
    return Promise.resolve({ guest: true });
  }

  /**
  * Wrapper for fetch() that will attempt to add a Bearer token if present.
  *
  * If there's a valid token, or one can be obtained via a refresh token, then
  * add it to the request headers. If not, issue the request without adding an
  * Authorization header.
  *
  * @param {string} url URL to fetch.
  * @param {object} options Options for fetch().
  *
  * returns {object} Response as JSON or error
  */
  async function fetchWithAuthentication(url, method, body) {
    const options = {
      method,
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'include',
      headers: new Headers({
        'X-Consumer-ID': process.env.REACT_APP_DRUPAL_CONSUMER_UUID,
      }),
    };

    if (body) {
      options.body = body;
      options.headers.append('Content-Type', 'application/vnd.api+json');
    }

    if (!options.headers.get('Authorization')) {
      try {
        const oauthToken = await getLocalStorageToken(config.token_name, refreshPromises, config);
        if (oauthToken) {
          options.headers.append('Authorization', `Bearer ${oauthToken.access_token}`);
        }
      } catch (error) {
        // Error getting OAuth token
      }
    }

    let combinedData = null;

    const fullUrl = `${config.base}${url}`;
    const res = await fetch(fullUrl, options);

    combinedData = await res.json();

    let hasMore = combinedData?.links?.next;
    let moreLink = combinedData?.links?.next?.href;
    while (hasMore) {
      // Sequential by nature: each page's `next` link is only known once the
      // previous page has come back, so these cannot be parallelised.
      // eslint-disable-next-line no-await-in-loop
      const responseMore = await fetch(moreLink, options);
      // eslint-disable-next-line no-await-in-loop
      const dataMore = await responseMore.json();
      const dataMoreData = dataMore.data || [];
      const dataMoreIncluded = dataMore.included || [];
      const prevData = combinedData?.data || [];
      const prevIncluded = combinedData?.included || [];
      combinedData.data = [...prevData, ...dataMoreData];
      combinedData.included = [...prevIncluded, ...dataMoreIncluded];
      hasMore = dataMore?.links?.next;
      moreLink = dataMore?.links?.next?.href;
    }

    return combinedData;
  }

  /**
  * Wrapper for fetch() that will attempt to add a Bearer token if present.
  *
  * If there's a valid token, or one can be obtained via a refresh token, then
  * add it to the request headers. If not, issue the request without adding an
  * Authorization header.
  *
  * @param {string} url URL to fetch.
  * @param {object} options Options for fetch().
  *
  * returns {object} Response as JSON or error
  */
  async function fetchXmlWithAuthentication(url) {
    const options = {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'include',
      headers: new Headers({
        'X-Consumer-ID': process.env.REACT_APP_DRUPAL_CONSUMER_UUID,
      }),
    };

    if (!options.headers.get('Authorization')) {
      const oauthToken = await getLocalStorageToken(config.token_name, refreshPromises, config);
      if (oauthToken) {
        options.headers.append('Authorization', `Bearer ${oauthToken.access_token}`);
      }
    }

    try {
      const fullUrl = `${url}`;
      const res = await fetch(fullUrl, options);
      const text = await res.text();
      return text;
    } catch (error) {
      // safe to allow this error
      return undefined;
    }
  }

  /**
  * Wrapper for fetch() that will attempt to add a Bearer token if present.
  *
  * If there's a valid token, or one can be obtained via a refresh token, then
  * add it to the request headers. If not, issue the request without adding an
  * Authorization header.
  *
  * @param {string} url URL to fetch.
  * @param {object} options Options for fetch().
  *
  * returns {object} Response as JSON or error
  */
  async function fetchImgWithAuthentication(url) {
    const options = {
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'include',
      headers: new Headers({
        'X-Consumer-ID': process.env.REACT_APP_DRUPAL_CONSUMER_UUID,
      }),
    };

    if (!options.headers.get('Authorization')) {
      const oauthToken = await getLocalStorageToken(config.token_name, refreshPromises, config);
      if (oauthToken) {
        options.headers.append('Authorization', `Bearer ${oauthToken.access_token}`);
      }
    }

    try {
      const fullUrl = `${config.base}${url}`;
      const res = await fetch(fullUrl, options);
      const blob = await res.blob();
      return blob;
    } catch (error) {
      // safe to allow this error
      return undefined;
    }
  }

  /**
  * Check if the current user is logged in or not.
  * If they are logged in, return their username
  *
  * @returns {Promise}
  */
  async function isLoggedIn() {
    const currentToken = await getLocalStorageToken(config.token_name, refreshPromises, config);
    if (currentToken) {
      return Promise.resolve(currentToken);
    }
    return Promise.resolve(false);
  }

  return {
    preLogin,
    login,
    noLoginAuth,
    logout,
    isLoggedIn,
    fetchWithAuthentication,
    fetchXmlWithAuthentication,
    fetchImgWithAuthentication,
  };
}
